import {
  Required,
  Schema,
  TextField,
  EnumField,
  Type,
  AccessContext,
  DynamicField,
  DynamicColumn
} from "lego-gui-lib";
import { t } from "../../../core/i18n/i18n";
import { IvrMenuFK } from "../schema";
import { api, mainStore } from "../../../core/mainStore";
import { RemoteChildDataSource } from "lego-gui-lib/lib/dataAccess/RemoteChildDataSource";
import { CustomerMessagesFK } from "../../message/schema";
import { SubTreeFK } from "../../tree/schema";
import { Auth } from "../../../core/model/auth";
import { queryParamDtoStrategy } from "lego-gui-lib/lib/dataAccess/ChildDataSourceStrategies";
import { FormState } from "final-form";
import { DigitsEnum } from "../../../common/common";

export enum Action {
  NEXT_MENU = 0,
  FINAL_ANNOUNCEMENT = 1,
  SUBTREE = 2
}

export enum IVRValueType {
  TIMEOUT = "timeout",
  BAD_DIGIT = "bad_digit"
}

export function UserInputField(): Type {
  return EnumField(
    [
      {
        label: t("hns:ivrMenuUserInput:ivrValueType:timeout"),
        value: IVRValueType.TIMEOUT
      },
      {
        label: t("hns:ivrMenuUserInput:ivrValueType:badDigit"),
        value: IVRValueType.BAD_DIGIT
      },
      ...DigitsEnum
    ],
    Required
  );
}

export function ActionField(): Type {
  return EnumField(
    [
      {
        label: t("hns:ivrMenuUserInput:action:next_menu"),
        value: Action.NEXT_MENU
      },
      {
        label: t("hns:ivrMenuUserInput:action:final_announcement"),
        value: Action.FINAL_ANNOUNCEMENT
      },
      {
        label: t("hns:ivrMenuUserInput:action:subtree"),
        value: Action.SUBTREE
      }
    ],
    Required
  );
}

export const IvrMenuUserInputSchema = new Schema<DynamicField>({
  primaryKey: ["id"],
  security: {
    readableRole: Auth.Roles.ANYONE,
    editableRole: Auth.Roles.ANYONE_WITH_WRITE
  },
  fields: [
    {
      name: "userInput",
      type: UserInputField(),
      title: t("hns:ivrMenuUserInput:user_input")
    },
    {
      name: "action",
      type: ActionField(),
      title: t("hns:ivrMenuUserInput:action:domain")
    },
    {
      name: "nextIvrMenuRef",
      type: IvrMenuFK,
      title: t("hns:ivrMenuUserInput:next_ivr_menu_ref")
    },
    {
      name: "msgFinalRef",
      type: CustomerMessagesFK,
      title: t("hns:ivrMenuUserInput:final_message")
    },
    {
      name: "subTreeRef",
      type: SubTreeFK,
      title: t("hns:tree:treeBlock:subtree")
    }
  ]
});

const onActionChange = (
  newValue: any,
  oldValue: any,
  formState: FormState<any>
) => {
  formState.values["nextIvrMenuRef"] = null;
  formState.values["msgFinalRef"] = null;
  formState.values["subTreeRef"] = null;
};

export const EditIvrMenuUserInputSchema = IvrMenuUserInputSchema.extend<
  DynamicField
>({
  action: {
    type: ActionField(),
    onChange: onActionChange
  },
  nextIvrMenuRef: {
    if: (ctx: AccessContext) => ctx.data?.action === Action.NEXT_MENU,
    type: IvrMenuFK,
    title: t("hns:ivrMenuUserInput:next_ivr_menu_ref")
  },
  msgFinalRef: {
    if: (ctx: AccessContext) => ctx.data?.action === Action.FINAL_ANNOUNCEMENT,
    type: CustomerMessagesFK,
    title: t("hns:ivrMenuUserInput:final_message")
  },
  subTreeRef: {
    if: (ctx: AccessContext) => ctx.data?.action === Action.SUBTREE,
    type: SubTreeFK,
    title: t("hns:tree:treeBlock:subtree")
  }
});

export const IvrMenuUserInputListSchema = new Schema<DynamicColumn>({
  fields: [
    {
      name: "userInput",
      type: UserInputField(),
      title: t("hns:ivrMenuUserInput:user_input"),
      filter: false,
      sort: false
    },
    {
      name: "action",
      type: ActionField(),
      title: t("hns:ivrMenuUserInput:action:domain"),
      filter: false,
      sort: false
    },
    {
      name: "actionParam",
      // this column will match when generating table headers
      type: TextField(), // does not matter, it is just a placeholder as `type` does not allow null value
      if: (ctx: AccessContext) => ctx.data?.action === undefined,
      filter: false,
      sort: false
    },
    // remaining columns will match only when generating table rows
    // and only one at a time
    {
      ...EditIvrMenuUserInputSchema.Named["msgFinalRef"]
    },
    {
      ...EditIvrMenuUserInputSchema.Named["nextIvrMenuRef"]
    },
    {
      ...EditIvrMenuUserInputSchema.Named["subTreeRef"]
    }
  ],
  security: IvrMenuUserInputSchema.security,
  primaryKey: IvrMenuUserInputSchema.primaryKey
});

mainStore.ivrMenuUserInput = RemoteChildDataSource.create({
  api: api,
  name: "ivrMenuUserInput",
  schema: IvrMenuUserInputSchema,
  url: "/userInputs",
  commons: mainStore.commons,
  parent: mainStore.ivrMenu.oneDataSource,
  dtoStrategy: queryParamDtoStrategy("id", "ivrMenuRef")
});
